import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FolderDeleteOutlinedIcon from "@mui/icons-material/FolderDeleteOutlined";
import { useShallow } from "zustand/react/shallow";
import { TrashModel } from "target_wasm";
import { LoadingState } from "@/components/common/LoadingState";
import { SearchBar } from "@/components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { ModelRow } from "@/components/main/overview/ModelRow";
import { ListEmptyShell } from "@/components/main/overview/ListEmptyShell";
import { VersionChip } from "@/components/main/utils/common/VersionChip";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import { trashModelToModelElement } from "@/components/main/utils/models/types";
import { restoreSpace, type TrashedSpace } from "@/lib/api/organizations";
import { deletionCountdown, formatDayMonthYear } from "@/lib/dates";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { useTrashStore } from "@/components/main/stores/trashStore";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import {
  ColumnBottomSpacer,
  ColumnTopSpacer,
  LIST_MAX_HEIGHT,
} from "@/components/main/pages/pageColumn";

/// D53: one deleted subtree, one group. `header` is the doomed root; the
/// models are every trashed model under it, sub-spaces included.
interface TrashGroup {
  header: TrashedSpace;
  models: TrashModel[];
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/// Main-app Trash page. Mirrors the Overview / Datasets shell: a
/// centered column with a fixed filter bar and a scrolling list whose section
/// header sticks. Rows reuse the Overview `ModelRow` in trash mode — same icon,
/// name, spacing, and tokens — with Restore / Delete-forever inline. No add
/// affordance, and a trashed model can't be entered. A deleted space is one
/// group with one Restore, and everything under it comes back together (D53).
export const TrashPage: React.FC = () => {
  const {
    trashModels,
    trashedSpaces,
    lastFetch,
    startPolling,
    fetchTrashModels,
    removeTrashedSpaces,
    refreshTrashModels,
  } = useTrashStore(
    useShallow((s) => ({
      trashModels: s.trashModels,
      trashedSpaces: s.trashedSpaces,
      lastFetch: s.lastTrashModelsFetch,
      startPolling: s.startPolling,
      fetchTrashModels: s.fetchTrashModels,
      removeTrashedSpaces: s.removeTrashedSpaces,
      refreshTrashModels: s.refreshTrashModels,
    })),
  );
  const { fetchModelsList, restoreModel, purgeModel } = useMainStateStore(
    useShallow((s) => ({
      fetchModelsList: s.fetchModelsList,
      restoreModel: s.restoreModel,
      purgeModel: s.purgeModel,
    })),
  );
  const refreshSpaces = useOrgContextStore((s) => s.refreshSpaces);
  // S6c/D41: the Trash is org-scoped, so a context switch is a new list.
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  const [filter, setFilter] = useState("");
  const [confirmTarget, setConfirmTarget] = useState<TrashModel | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [restoringSpace, setRestoringSpace] = useState<string | null>(null);

  useEffect(() => startPolling(), [startPolling]);
  useEffect(() => {
    void fetchTrashModels();
  }, [fetchTrashModels, activeOrgId]);

  const q = filter.trim().toLowerCase();
  const { groups, loose } = useMemo(() => {
    // A model's stamp names the doomed space it sits in; the group is that
    // space's ROOT, which is the only one with a header.
    const rootOf = new Map<string, TrashedSpace>();
    for (const root of trashedSpaces) {
      rootOf.set(root.space_id, root);
      for (const sub of root.sub_spaces) rootOf.set(sub.space_id, root);
    }
    const byRoot = new Map<string, TrashGroup>(
      trashedSpaces.map((h) => [h.space_id, { header: h, models: [] }]),
    );
    const loose: TrashModel[] = [];
    for (const m of trashModels) {
      if (
        q &&
        !(m.model_name_js || "Unnamed Model").toLowerCase().includes(q)
      ) {
        continue;
      }
      const root = m.via_space_id_js
        ? rootOf.get(m.via_space_id_js)
        : undefined;
      if (!root) {
        loose.push(m);
        continue;
      }
      byRoot.get(root.space_id)?.models.push(m);
    }
    const groups = [...byRoot.values()].filter(
      (g) =>
        !q ||
        g.models.length > 0 ||
        g.header.name.toLowerCase().includes(q) ||
        g.header.sub_spaces.some((s) => s.name.toLowerCase().includes(q)),
    );
    return { groups, loose };
  }, [trashModels, trashedSpaces, q]);

  const restore = useCallback(
    async (model: TrashModel) => {
      const outcome = await restoreModel(model.model_id_js);
      showNotificationGlobal(
        outcome.ok ? "Model restored from trash" : outcome.message,
        outcome.ok ? "success" : "error",
      );
    },
    [restoreModel],
  );

  const restoreWholeSpace = useCallback(
    async (group: TrashGroup) => {
      setRestoringSpace(group.header.space_id);
      try {
        const { spaces_restored, models_restored } = await restoreSpace(
          group.header.space_id,
        );
        const subSpaces = Math.max(spaces_restored.length - 1, 0);
        const parts = [
          subSpaces > 0 && plural(subSpaces, "sub-space"),
          models_restored > 0 && plural(models_restored, "model"),
        ].filter(Boolean);
        showNotificationGlobal(
          parts.length > 0
            ? `${group.header.name} restored with ${parts.join(" and ")}`
            : `${group.header.name} restored`,
          "success",
        );
        removeTrashedSpaces(spaces_restored);
        void fetchModelsList({ refresh: true });
        void refreshSpaces();
      } catch (e) {
        showNotificationGlobal(
          e instanceof Error ? e.message : "Could not restore the space",
          "error",
        );
      } finally {
        setRestoringSpace(null);
      }
    },
    [removeTrashedSpaces, fetchModelsList, refreshSpaces],
  );

  const confirmDelete = useCallback(async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    const ok = await purgeModel(confirmTarget.model_id_js);
    setDeleting(false);
    setConfirmTarget(null);
    showNotificationGlobal(
      ok ? "Model permanently deleted" : "Failed to permanently delete model",
      ok ? "success" : "error",
    );
  }, [confirmTarget, purgeModel]);

  const row = (m: TrashModel, group: TrashGroup | null) => {
    const element = trashModelToModelElement(m);
    if (!element) return null;
    const countdown = deletionCountdown(m.scheduled_deletion_date_js);
    return (
      <ModelRow
        key={m.model_id_js}
        model={element}
        trash={{
          countdownLabel: countdown.label,
          countdownTooltip: countdown.tooltip,
          onRestore: () => void restore(m),
          onDeleteForever: () => setConfirmTarget(m),
          trashModel: m,
          // A transfer changes which tables are still scheduled, so re-read
          // the list on close.
          onAnalysisClosed: () => void refreshTrashModels(),
          // A model in a doomed space comes back only with the space (D53).
          // Without a group the caller does not manage that space, so the
          // restore is someone else's — the door would 409 anyway.
          restoreLocked: group
            ? `Comes back with ${group.header.name} — restore the space`
            : m.via_space_name_js
              ? `Comes back with ${m.via_space_name_js} — a manager of that space restores it`
              : undefined,
        }}
      />
    );
  };

  const groupLine = (g: TrashGroup) => {
    const by = g.header.deleted_by ? ` by ${g.header.deleted_by}` : "";
    const subs = g.header.sub_spaces;
    const with_ =
      subs.length > 0
        ? ` · with ${plural(subs.length, "sub-space")}: ${subs.map((s) => s.name).join(", ")}`
        : "";
    const org = g.header.with_account
      ? " · Goes with your account — cancel the account deletion in Settings"
      : g.header.with_org
        ? ` · Goes with the organization ${g.header.with_org} — an owner or admin cancels that from its settings`
        : "";
    return `Deleted${by} on ${formatDayMonthYear(g.header.deletion_scheduled_at)} · restorable until ${formatDayMonthYear(g.header.purge_at)}${with_}${org}`;
  };

  // Loader until the first fetch resolves (the poll recovers from a
  // transient failure); the store keeps the list warm across navigations, so
  // re-opening Trash never re-flashes the loader or the empty state.
  if (lastFetch === null) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoadingState size="large" isLoading label="Loading trash..." />
      </div>
    );
  }

  const isEmpty = trashModels.length === 0 && trashedSpaces.length === 0;
  const nothingMatches = loose.length === 0 && groups.length === 0;

  return (
    <div className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6">
      <ColumnTopSpacer />
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: "45rem",
          paddingTop: "2.5rem",
          paddingBottom: "2.5rem",
        }}
      >
        {isEmpty ? (
          <ListEmptyShell
            icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Trash"
            hint="Deleted models and spaces rest here for 30 days before they're permanently removed."
            countLabel="0 models"
            search={{
              value: filter,
              onChange: setFilter,
              placeholder: "Search trash…",
            }}
          />
        ) : (
          <>
            <div className="shrink-0 mb-6">
              <SearchBar
                value={filter}
                onChange={setFilter}
                placeholder="Search trash…"
                size="lg"
              />
            </div>

            <div
              className="overflow-y-auto"
              style={{ maxHeight: LIST_MAX_HEIGHT }}
            >
              {nothingMatches ? (
                <div className="text-center py-12 text-small text-fg-muted italic">
                  Nothing matches “{filter.trim()}”.
                </div>
              ) : (
                <>
                  {loose.length > 0 && (
                    <div className="mb-8">
                      <div className="sticky top-0 z-10 bg-bg overflow-hidden">
                        <SectionRule
                          inset
                          icon={
                            <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
                          }
                          label="Trash"
                        />
                      </div>
                      {loose.map((m) => row(m, null))}
                    </div>
                  )}
                  {groups.map((g) => (
                    <div
                      key={g.header.space_id}
                      className="mb-8"
                      data-testid={`trash-group-${g.header.space_id}`}
                    >
                      <div className="sticky top-0 z-10 bg-bg overflow-hidden">
                        <SectionRule
                          inset
                          icon={
                            <FolderDeleteOutlinedIcon sx={{ fontSize: 14 }} />
                          }
                          label={g.header.name}
                          trailing={
                            g.models.length > 0
                              ? plural(g.models.length, "model")
                              : undefined
                          }
                          action={
                            g.header.cancellable
                              ? {
                                  label:
                                    restoringSpace === g.header.space_id
                                      ? "Restoring…"
                                      : "Restore space",
                                  onClick: () => void restoreWholeSpace(g),
                                  testId: `restore-space-${g.header.space_id}`,
                                }
                              : undefined
                          }
                        />
                      </div>
                      <div className="px-1 mb-2 text-fg-muted-regular-small">
                        {groupLine(g)}
                      </div>
                      {g.models.length === 0 ? (
                        <div className="px-1 py-2 text-small text-fg-muted italic">
                          No models — restoring brings back the space itself.
                        </div>
                      ) : (
                        g.models.map((m) => row(m, g))
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Pinned below the scroll so the count stays visible. */}
            <div className="shrink-0 flex items-center mt-6">
              <span
                className="font-mono text-fg-muted"
                style={{
                  fontSize: "var(--font-size-x-small)",
                  letterSpacing: "0.04em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {[
                  plural(trashModels.length, "model"),
                  trashedSpaces.length > 0 &&
                    plural(trashedSpaces.length, "space"),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          </>
        )}
      </div>

      <ColumnBottomSpacer />
      <div className="absolute bottom-4 right-4 z-10">
        <VersionChip />
      </div>

      <ConfirmDialog
        open={confirmTarget !== null}
        title="Permanently delete model"
        message={
          <p>
            Permanently delete{" "}
            <span className="text-fg-bold">
              {confirmTarget?.model_name_js || "Unnamed Model"}
            </span>
            ? This can&apos;t be undone.
          </p>
        }
        confirmText="Delete forever"
        confirmVariant="destructive"
        isLoading={deleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
};
